import { jsx as t, jsxs as p } from "react/jsx-runtime";
import * as l from "react";
import { mergeProps as h } from "@base-ui/react/merge-props";
import { useRender as v } from "@base-ui/react/use-render";
import { cva as I } from "class-variance-authority";
import { useIsMobile as T } from "../../hooks/use-mobile.js";
import { cn as r } from "../../lib/utils.js";
import { Button as E } from "./button.js";
import { Input as D } from "./input.js";
import { Separator as B } from "./separator.js";
import { Sheet as C, SheetContent as A, SheetHeader as O, SheetTitle as R, SheetDescription as H } from "./sheet.js";
import { Skeleton as _ } from "./skeleton.js";
import { TooltipTrigger as K, Tooltip as j, TooltipContent as G } from "./tooltip.js";
import { PanelLeftIcon as L } from "lucide-react";
const $ = "sidebar_state", P = 60 * 60 * 24 * 7, V = "16rem", W = "18rem", q = "3rem", F = "b", z = l.createContext(null);
function S() {
  const e = l.useContext(z);
  if (!e)
    throw new Error("useSidebar must be used within a SidebarProvider.");
  return e;
}
function oe({
  defaultOpen: e = !0,
  open: a,
  onOpenChange: i,
  className: s,
  style: n,
  children: g,
  ...b
}) {
  const u = T(), [f, o] = l.useState(!1), [N, M] = l.useState(e), m = a ?? N, x = l.useCallback(
    (d) => {
      const c = typeof d == "function" ? d(m) : d;
      i ? i(c) : M(c), document.cookie = `${$}=${c}; path=/; max-age=${P}`;
    },
    [i, m]
  ), w = l.useCallback(() => u ? o((d) => !d) : x((d) => !d), [u, x, o]);
  l.useEffect(() => {
    const d = (c) => {
      c.key === F && (c.metaKey || c.ctrlKey) && (c.preventDefault(), w());
    };
    return window.addEventListener("keydown", d), () => window.removeEventListener("keydown", d);
  }, [w]);
  const y = m ? "expanded" : "collapsed", k = l.useMemo(
    () => ({
      state: y,
      open: m,
      setOpen: x,
      isMobile: u,
      openMobile: f,
      setOpenMobile: o,
      toggleSidebar: w
    }),
    [y, m, x, u, f, o, w]
  );
  return /* @__PURE__ */ t(z.Provider, { value: k, children: /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar-wrapper",
      style: {
        "--sidebar-width": V,
        "--sidebar-width-icon": q,
        ...n
      },
      className: r(
        "group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar",
        s
      ),
      ...b,
      children: g
    }
  ) });
}
function le({
  side: e = "left",
  variant: a = "sidebar",
  collapsible: i = "offcanvas",
  className: s,
  children: n,
  dir: g,
  ...b
}) {
  const { isMobile: u, state: f, openMobile: o, setOpenMobile: N } = S();
  return i === "none" ? /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar",
      className: r(
        "flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground",
        s
      ),
      ...b,
      children: n
    }
  ) : u ? /* @__PURE__ */ t(C, { open: o, onOpenChange: N, ...b, children: /* @__PURE__ */ p(
    A,
    {
      dir: g,
      "data-sidebar": "sidebar",
      "data-slot": "sidebar",
      "data-mobile": "true",
      className: "w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden",
      style: {
        "--sidebar-width": W
      },
      side: e,
      children: [
        /* @__PURE__ */ p(O, { className: "sr-only", children: [
          /* @__PURE__ */ t(R, { children: "Sidebar" }),
          /* @__PURE__ */ t(H, { children: "Displays the mobile sidebar." })
        ] }),
        /* @__PURE__ */ t("div", { className: "flex h-full w-full flex-col", children: n })
      ]
    }
  ) }) : /* @__PURE__ */ p(
    "div",
    {
      className: "group peer hidden text-sidebar-foreground md:block",
      "data-state": f,
      "data-collapsible": f === "collapsed" ? i : "",
      "data-variant": a,
      "data-side": e,
      "data-slot": "sidebar",
      children: [
        /* @__PURE__ */ t(
          "div",
          {
            "data-slot": "sidebar-gap",
            className: r(
              "relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear",
              "group-data-[collapsible=offcanvas]:w-0",
              "group-data-[side=right]:rotate-180",
              a === "floating" || a === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)"
            )
          }
        ),
        /* @__PURE__ */ t(
          "div",
          {
            "data-slot": "sidebar-container",
            "data-side": e,
            className: r(
              "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear data-[side=left]:left-0 data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)] data-[side=right]:right-0 data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)] md:flex",
              // Adjust the padding for floating and inset variants.
              a === "floating" || a === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]" : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
              s
            ),
            ...b,
            children: /* @__PURE__ */ t(
              "div",
              {
                "data-sidebar": "sidebar",
                "data-slot": "sidebar-inner",
                className: "flex size-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1 group-data-[variant=floating]:ring-sidebar-border",
                children: n
              }
            )
          }
        )
      ]
    }
  );
}
function ue({
  className: e,
  onClick: a,
  ...i
}) {
  const { toggleSidebar: s } = S();
  return /* @__PURE__ */ p(
    E,
    {
      "data-sidebar": "trigger",
      "data-slot": "sidebar-trigger",
      variant: "ghost",
      size: "icon-sm",
      className: r(e),
      onClick: (n) => {
        a == null || a(n), s();
      },
      ...i,
      children: [
        /* @__PURE__ */ t(L, {}),
        /* @__PURE__ */ t("span", { className: "sr-only", children: "Toggle Sidebar" })
      ]
    }
  );
}
function ce({ className: e, ...a }) {
  const { toggleSidebar: i } = S();
  return /* @__PURE__ */ t(
    "button",
    {
      "data-sidebar": "rail",
      "data-slot": "sidebar-rail",
      "aria-label": "Toggle Sidebar",
      tabIndex: -1,
      onClick: i,
      title: "Toggle Sidebar",
      className: r(
        "absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] hover:after:bg-sidebar-border sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-sidebar",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        e
      ),
      ...a
    }
  );
}
function be({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "main",
    {
      "data-slot": "sidebar-inset",
      className: r(
        "relative flex w-full flex-1 flex-col bg-background md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        e
      ),
      ...a
    }
  );
}
function fe({
  className: e,
  ...a
}) {
  return /* @__PURE__ */ t(
    D,
    {
      "data-slot": "sidebar-input",
      "data-sidebar": "input",
      className: r("h-8 w-full bg-background shadow-none", e),
      ...a
    }
  );
}
function pe({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar-header",
      "data-sidebar": "header",
      className: r("flex flex-col gap-2 p-2", e),
      ...a
    }
  );
}
function ge({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar-footer",
      "data-sidebar": "footer",
      className: r("flex flex-col gap-2 p-2", e),
      ...a
    }
  );
}
function me({
  className: e,
  ...a
}) {
  return /* @__PURE__ */ t(
    B,
    {
      "data-slot": "sidebar-separator",
      "data-sidebar": "separator",
      className: r("mx-2 w-auto bg-sidebar-border", e),
      ...a
    }
  );
}
function he({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar-content",
      "data-sidebar": "content",
      className: r(
        "no-scrollbar flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        e
      ),
      ...a
    }
  );
}
function ve({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar-group",
      "data-sidebar": "group",
      className: r("relative flex w-full min-w-0 flex-col p-2", e),
      ...a
    }
  );
}
function xe({
  className: e,
  render: a,
  ...i
}) {
  return v({
    defaultTagName: "div",
    props: h(
      {
        className: r(
          "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 ring-sidebar-ring outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
          e
        )
      },
      i
    ),
    render: a,
    state: {
      slot: "sidebar-group-label",
      sidebar: "group-label"
    }
  });
}
function we({
  className: e,
  render: a,
  ...i
}) {
  return v({
    defaultTagName: "button",
    props: h(
      {
        className: r(
          "absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0",
          e
        )
      },
      i
    ),
    render: a,
    state: {
      slot: "sidebar-group-action",
      sidebar: "group-action"
    }
  });
}
function Se({
  className: e,
  ...a
}) {
  return /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar-group-content",
      "data-sidebar": "group-content",
      className: r("w-full text-sm", e),
      ...a
    }
  );
}
function Ne({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "ul",
    {
      "data-slot": "sidebar-menu",
      "data-sidebar": "menu",
      className: r("flex w-full min-w-0 flex-col gap-0", e),
      ...a
    }
  );
}
function ye({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "li",
    {
      "data-slot": "sidebar-menu-item",
      "data-sidebar": "menu-item",
      className: r("group/menu-item relative", e),
      ...a
    }
  );
}
const U = I(
  "peer/menu-button group/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm ring-sidebar-ring outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:font-medium data-active:text-sidebar-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&>span:last-child]:truncate",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]"
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function _e({
  render: e,
  isActive: a = !1,
  variant: i = "default",
  size: s = "default",
  tooltip: n,
  className: g,
  ...b
}) {
  const { isMobile: u, state: f } = S(), o = v({
    defaultTagName: "button",
    props: h(
      {
        className: r(U({ variant: i, size: s }), g)
      },
      b
    ),
    render: n ? /* @__PURE__ */ t(K, { render: e }) : e,
    state: {
      slot: "sidebar-menu-button",
      sidebar: "menu-button",
      size: s,
      active: a
    }
  });
  return n ? (typeof n == "string" && (n = {
    children: n
  }), /* @__PURE__ */ p(j, { children: [
    o,
    /* @__PURE__ */ t(
      G,
      {
        side: "right",
        align: "center",
        hidden: f !== "collapsed" || u,
        ...n
      }
    )
  ] })) : o;
}
function ze({
  className: e,
  render: a,
  showOnHover: i = !1,
  ...s
}) {
  return v({
    defaultTagName: "button",
    props: h(
      {
        className: r(
          "absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0",
          i && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-active/menu-button:text-sidebar-accent-foreground aria-expanded:opacity-100 md:opacity-0",
          e
        )
      },
      s
    ),
    render: a,
    state: {
      slot: "sidebar-menu-action",
      sidebar: "menu-action"
    }
  });
}
function Me({
  className: e,
  ...a
}) {
  return /* @__PURE__ */ t(
    "div",
    {
      "data-slot": "sidebar-menu-badge",
      "data-sidebar": "menu-badge",
      className: r(
        "pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums select-none group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 peer-data-active/menu-button:text-sidebar-accent-foreground",
        e
      ),
      ...a
    }
  );
}
function ke({
  className: e,
  showIcon: a = !1,
  ...i
}) {
  const [s] = l.useState(() => `${Math.floor(Math.random() * 40) + 50}%`);
  return /* @__PURE__ */ p(
    "div",
    {
      "data-slot": "sidebar-menu-skeleton",
      "data-sidebar": "menu-skeleton",
      className: r("flex h-8 items-center gap-2 rounded-md px-2", e),
      ...i,
      children: [
        a && /* @__PURE__ */ t(
          _,
          {
            className: "size-4 rounded-md",
            "data-sidebar": "menu-skeleton-icon"
          }
        ),
        /* @__PURE__ */ t(
          _,
          {
            className: "h-4 max-w-(--skeleton-width) flex-1",
            "data-sidebar": "menu-skeleton-text",
            style: {
              "--skeleton-width": s
            }
          }
        )
      ]
    }
  );
}
function Ie({ className: e, ...a }) {
  return /* @__PURE__ */ t(
    "ul",
    {
      "data-slot": "sidebar-menu-sub",
      "data-sidebar": "menu-sub",
      className: r(
        "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5 group-data-[collapsible=icon]:hidden",
        e
      ),
      ...a
    }
  );
}
function Te({
  className: e,
  ...a
}) {
  return /* @__PURE__ */ t(
    "li",
    {
      "data-slot": "sidebar-menu-sub-item",
      "data-sidebar": "menu-sub-item",
      className: r("group/menu-sub-item relative", e),
      ...a
    }
  );
}
function Ee({
  render: e,
  size: a = "md",
  isActive: i = !1,
  className: s,
  ...n
}) {
  return v({
    defaultTagName: "a",
    props: h(
      {
        className: r(
          "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground ring-sidebar-ring outline-hidden group-data-[collapsible=icon]:hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[size=md]:text-sm data-[size=sm]:text-xs data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
          s
        )
      },
      n
    ),
    render: e,
    state: {
      slot: "sidebar-menu-sub-button",
      sidebar: "menu-sub-button",
      size: a,
      active: i
    }
  });
}
export {
  le as Sidebar,
  he as SidebarContent,
  ge as SidebarFooter,
  ve as SidebarGroup,
  we as SidebarGroupAction,
  Se as SidebarGroupContent,
  xe as SidebarGroupLabel,
  pe as SidebarHeader,
  fe as SidebarInput,
  be as SidebarInset,
  Ne as SidebarMenu,
  ze as SidebarMenuAction,
  Me as SidebarMenuBadge,
  _e as SidebarMenuButton,
  ye as SidebarMenuItem,
  ke as SidebarMenuSkeleton,
  Ie as SidebarMenuSub,
  Ee as SidebarMenuSubButton,
  Te as SidebarMenuSubItem,
  oe as SidebarProvider,
  ce as SidebarRail,
  me as SidebarSeparator,
  ue as SidebarTrigger,
  S as useSidebar
};
//# sourceMappingURL=sidebar.js.map
